import type { ServicePageConfig } from "../../../types";
import {
  getPackagePricingRows,
  PACKAGE_PRICE_HINT_SV,
  PACKAGE_PRICE_HINT_EN,
} from "../../../package-pricing";
import { getCityMunicipalityList } from "../../../cities";

const stockholmKommunerText = getCityMunicipalityList("stockholm");

export const flytthjalpVarmdoPage: ServicePageConfig = {
  slug: "flytthjalp-varmdo",
  serviceCategory: "flytt",
  city: "stockholm",

  meta: {
    title: "Flytthjälp Värmdö – Gustavsberg till Djurö | Ungdomskraft",
    description:
      "Boka flytthjälp på Värmdö: Gustavsberg, Hemmesta, Brunn, Ingarö, Djurö och Stavsnäs. Skåpbil och bärare till alla adresser med väg. Fast paketpris.",
    h1: "Flytthjälp Värmdö",
  },

  hero: {
    heading: "Flytthjälp på Värmdö",
    intro:
      "Ska du flytta till lägenhet i Gustavsberg, till ett hus på Ingarö eller till ett före detta sommarhus ute mot Djurö? Vi kommer med skåpbil och bärare längs Värmdöleden och ända fram till tomten, så länge det finns väg dit. Paketpriset ser du när du bokar.",
    heroImage: "/hero-flytthjalp.png",
    heroImageAlt: "Ungdomskraft bär möbler till hus vid flytthjälp på Värmdö",
  },

  featureCards: {
    sinceText: "Flytthjälp sedan 2014",
    storyText:
      "Ungdomskraft har flyttat hushåll i Stockholms län sedan 2014. På Värmdö är det sällan en rak väg från bil till dörr – smala grusvägar, sluttande tomter och trappor i berget hör till – och våra bärare tar det i sin takt, med filtar och remmar.",
    bookYourText: "Boka flytt på Värmdö",
    bookDescText:
      "Skriv in båda adresserna, beskriv vad som ska med och välj paket och tid. Du ser priset innan du bekräftar.",
    vehicleTitleText: "Skåpbil hela vägen ut",
    vehicleDescText:
      "Vi kör skåpbilen från din gamla adress ut på Värmdö och lossar vid huset. Du behöver inte hyra bil eller köra fram och tillbaka över Skurubron själv.",
  },

  itemsSection: {
    heading: "Det här flyttar vi ofta på Värmdö",
    items: [
      {
        name: "Soffor & fåtöljer",
        description: "Soffor och fåtöljer, inlindade för bärning på grus och trappor",
        image: "/sofa.webp",
        alt: "Soffa som bärs till hus på Ingarö, Värmdö",
      },
      {
        name: "Sängar & madrasser",
        description: "Sängar, bäddsoffor och gästsängar till sommarhuset",
        image: "/bed.webp",
        alt: "Säng som flyttas från lägenhet i Gustavsberg, Värmdö",
      },
      {
        name: "Bord & stolar",
        description: "Matbord, trädgårdsmöbler och stolar",
        image: "/table.webp",
        alt: "Trädgårdsmöbler som lastas vid flytt på Djurö, Värmdö",
      },
      {
        name: "Vitvaror",
        description: "Kyl, frys, tvättmaskin och torktumlare",
        image: "/washer.webp",
        alt: "Frys som flyttas till hus i Hemmesta, Värmdö",
      },
      {
        name: "Garderober & hyllor",
        description: "Garderober, byråer och bokhyllor",
        image: "/wardrobe.webp",
        alt: "Bokhylla som bärs vid flytt i Brunn, Värmdö",
      },
      {
        name: "Flyttkartonger",
        description: "Kartonger med kök, böcker, verktyg och båtprylar",
        image: "/box.webp",
        alt: "Flyttkartonger som lastas i skåpbil i Stavsnäs, Värmdö",
      },
    ],
  },

  seoContent: {
    mainHeading: "Flytthjälp på Värmdö – från Gustavsberg ut mot skärgården",
    mainText:
      "Värmdö är en skärgårdskommun där bebyggelsen spänner från flerbostadshusen i Gustavsberg, med sin historia som porslinsbruksort vid Farstaviken, till villor och radhus i Hemmesta och Brunn och tusentals hus som en gång byggdes som sommarstugor. Många av dem är i dag åretruntbostäder, på Ingarö, runt Djurö och ut mot Stavsnäs. Det gör flyttarna här annorlunda än inne i stan: vägen fram till huset är ofta en smal grusväg, tomten sluttar mot vattnet och det kan vara en bit att bära från där bilen kan stanna. Vi frågar om det i förväg så att rätt antal bärare kommer och tiden räcker.\n\nDen vanligaste flytten vi gör till Värmdö är från en lägenhet i Stockholm eller Nacka till ett hus på ön – ofta en familj som vill ha mer plats och närhet till vattnet. Vägen ut går via Skurubron och Värmdöleden (väg 222), som sedan fortsätter förbi Mölnvik och Strömma till Stavsnäs. Till Ingarö kör vi väg 646 från Gustavsberg. Sträckan från innerstaden är längre än de 20 km som ingår i paketpriset, så räkna med ett kilometertillägg; du ser hela beloppet innan du bokar.\n\nVärmdö består av många öar, men vi flyttar bara till adresser som går att nå med bil. Hus på öar utan vägförbindelse – till exempel på Sandön eller Möja – kräver båttransport, och den erbjuder vi inte. Bor du på en enskild väg med vägförening, kontrollera om det finns begränsningar för tyngre fordon, framför allt under tjällossningen på våren, och berätta det när du bokar.",
    preparationHeading: "Förbered flytten på Värmdö",
    preparationText:
      "Packa lösa saker i kartonger och märk dem med rum. Töm garderober och byråer och frosta av frysen dagen innan. Går det att köra ända fram till huset? Om inte, visa oss var bilen kan stå, så bär vi därifrån – men det tar längre tid, så välj ett paket som räcker.\n\nKontrollera att vägen fram till tomten är framkomlig för en skåpbil: grindar ska gå att öppna, grenar inte hänga för lågt och vändplatsen vara fri. I Gustavsberg och andra lägenhetsområden, ta reda på var lastplatsen finns och om hissen kan låsas. Glöm inte bod, gäststuga och båthus om saker därifrån ska med. Ha nycklar och koder redo för båda adresserna.",
    additionalSections: [
      {
        heading: "Orter vi flyttar i, på Värmdö",
        content:
          "Vi flyttar till och från alla adresser på Värmdö som nås med bil, bland annat Gustavsberg, Hemmesta, Mölnvik, Brunn, Ingarö, Djurö och Stavsnäs.\n\nVi flyttar också mellan Värmdö och resten av Stockholmsregionen – Nacka, innerstaden och söderort är de vanligaste. Paketpriset gäller med 20 km körsträcka, och längre körning räknas per kilometer enligt paketet.",
      },
      {
        heading: "Från sommarhus till åretruntboende",
        content:
          "Många flyttar på Värmdö handlar om ett hus som blir helårsbostad. Då ska ofta ett helt hem från stan in i ett hus som redan har en del möbler – och något ska ut. Vi kan flytta in det nya och i samma veva köra det gamla till återvinning om du bokar bortforsling också.",
        cta: { text: "Se bortforsling i Stockholm →", href: "/bortforsling-stockholm" },
      },
      {
        heading: "Varför boka flytthjälp på Värmdö med Ungdomskraft?",
        listItems: [
          {
            label: "Vana vid grusvägar och sluttningar –",
            text: "Vi planerar för att bilen inte alltid kan stå vid dörren och bär hellre en extra runda än att stressa.",
          },
          {
            label: "Pris med körsträcka –",
            text: "Du ser paketpris och kilometertillägg direkt, innan du bokar. Ingen offert, inget timpris.",
          },
          {
            label: "Tydliga gränser –",
            text: "Vi kör till alla adresser med väg. Öar som kräver båt tar vi inte, och det får du veta direkt.",
          },
          {
            label: "Försäkrat –",
            text: "Uppdragen är försäkrade genom Trygg Hansa.",
          },
          {
            label: "RUT-avdrag –",
            text: "Halva arbetskostnaden dras av på fakturan, upp till 75 000 kr per person och år.",
          },
        ],
      },
    ],
  },

  faq: [
    {
      question: "Vad kostar flytthjälp på Värmdö?",
      answer:
        "Paketen kostar Liten 995 kr, Mellan 1 495 kr, Stor 2 495 kr, Extra Stor 4 995 kr (upp till 4 h) och Mega Stor 8 995 kr (upp till 8 h). 20 km körning ingår, och längre körning, som ofta blir fallet ut till Värmdö, räknas per kilometer. Totalen syns innan du bokar.",
    },
    {
      question: "Varför blir det ett kilometertillägg till Värmdö?",
      answer:
        "Paketpriset innehåller 20 km körsträcka. Många adresser på Värmdö ligger längre bort än så från stan, och då debiteras de extra kilometrarna enligt paketets pris per km.",
    },
    {
      question: "Kan ni flytta till hus på öar utan bro?",
      answer:
        "Nej. Vi kör till alla adresser som går att nå med bil, men erbjuder inte båttransport till öar som Sandön eller Möja.",
    },
    {
      question: "Kan ni köra på smala grusvägar?",
      answer:
        "Ja, våra skåpbilar klarar vanliga grusvägar. Berätta om vägen är smal, brant eller saknar vändplats, eller om vägföreningen har begränsningar.",
    },
    {
      question: "Kan jag få RUT-avdrag på flytthjälp på Värmdö?",
      answer:
        "Ja, 50 % av arbetskostnaden, upp till 75 000 kr per person och år. Vi gör avdraget på fakturan.",
    },
    {
      question: "Vad händer om bilen inte kan stå vid huset?",
      answer:
        "Då bär vi från närmaste plats där bilen kan stå. Det tar mer tid, så beskriv avståndet vid bokningen och välj ett paket som räcker.",
    },
    {
      question: "Hur snabbt kan ni komma ut till Värmdö?",
      answer:
        "Ofta inom några dagar. Lediga tider för just dina adresser ser du i bokningen.",
    },
    {
      question: "Ingår skåpbil?",
      answer:
        "Ja, från paketet Liten och uppåt ingår skåpbil och två bärare.",
    },
    {
      question: "Flyttar ni vitvaror?",
      answer:
        "Ja, kyl, frys, tvättmaskin och torktumlare. Frosta av och töm dem dagen innan.",
    },
    {
      question: "Tar ni isär och sätter ihop möbler?",
      answer:
        "Ja, det som inte går hela genom dörrar och trappor tar vi isär och sätter ihop på plats.",
    },
    {
      question: "Kan ni flytta på helgen?",
      answer:
        "Ja, vi flyttar alla dagar i veckan.",
    },
    {
      question: "Tar ni med saker från bod och båthus?",
      answer:
        "Ja, om det ska med till nya adressen bär vi det. Nämn det vid bokningen.",
    },
    {
      question: "Vilka delar av Värmdö täcker ni?",
      answer:
        "Alla adresser med vägförbindelse, bland annat Gustavsberg, Hemmesta, Mölnvik, Brunn, Ingarö, Djurö och Stavsnäs.",
    },
    {
      question: "Vilka andra kommuner flyttar ni i?",
      answer: `Hela Stockholmsregionen: ${stockholmKommunerText}. Samma prismodell överallt – paketpris plus körsträcka över 20 km.`,
    },
  ],

  priceHint: PACKAGE_PRICE_HINT_SV.transport,

  pricingRows: getPackagePricingRows("transport", "sv"),

  dynamicHeadings: {
    threeSteps: "Flytthjälp på Värmdö i 3 steg",
    step2Text:
      "Bärarna lastar skåpbilen vid din gamla adress och kör ut längs Värmdöleden till huset.",
    step3Title: "Inflyttat",
    step3Desc:
      "Vi bär in allt, även från vägen om bilen inte kommer ända fram, och sätter ihop det som tagits isär.",
    ctaHeading: "Boka flytthjälp på Värmdö",
    ctaText:
      "Fyll i adresserna, välj paket och tid. Du ser priset med körsträcka innan du bokar.",
  },

  en: {
    slug: "moving-help-varmdo",
    meta: {
      title: "Moving Help Värmdö – Gustavsberg to Djurö | Ungdomskraft",
      description:
        "Book moving help on Värmdö: Gustavsberg, Hemmesta, Brunn, Ingarö, Djurö and Stavsnäs. Van and carriers to every address with road access. Fixed price.",
      h1: "Moving Help Värmdö",
    },
    hero: {
      heading: "Moving Help on Värmdö",
      intro:
        "Moving to a flat in Gustavsberg, a house on Ingarö or a former summer house out towards Djurö? We come with a van and carriers along Värmdöleden and right up to the plot, as long as there is a road. You see the package price when you book.",
      heroImageAlt: "Ungdomskraft carrying furniture to a house on Värmdö",
    },
    featureCards: {
      sinceText: "Moving help since 2014",
      storyText:
        "Ungdomskraft has moved households in Stockholm County since 2014. On Värmdö there is rarely a straight line from van to door – narrow gravel roads, sloping plots and steps cut into the rock are part of it – and our carriers take it at their own pace, with blankets and straps.",
      bookYourText: "Book a move on Värmdö",
      bookDescText:
        "Enter both addresses, describe what is coming along and choose a package and time. You see the price before you confirm.",
      vehicleTitleText: "A van all the way out",
      vehicleDescText:
        "We drive the van from your old address out to Värmdö and unload at the house. No need to rent a vehicle or drive back and forth over Skurubron yourself.",
    },
    itemsSection: {
      heading: "What we often move on Värmdö",
      items: [
        { name: "Sofas & armchairs", description: "Sofas and armchairs, wrapped for carrying over gravel and steps", image: "/sofa.webp", alt: "Sofa carried to a house on Ingarö, Värmdö" },
        { name: "Beds & mattresses", description: "Beds, sofa beds and guest beds for the summer house", image: "/bed.webp", alt: "Bed moved from a flat in Gustavsberg, Värmdö" },
        { name: "Tables & chairs", description: "Dining tables, garden furniture and chairs", image: "/table.webp", alt: "Garden furniture loaded during a move on Djurö, Värmdö" },
        { name: "Appliances", description: "Fridge, freezer, washing machine and tumble dryer", image: "/washer.webp", alt: "Freezer moved to a house in Hemmesta, Värmdö" },
        { name: "Wardrobes & shelves", description: "Wardrobes, dressers and bookcases", image: "/wardrobe.webp", alt: "Bookcase carried during a move in Brunn, Värmdö" },
        { name: "Moving boxes", description: "Boxes with kitchenware, books, tools and boating gear", image: "/box.webp", alt: "Moving boxes loaded into a van in Stavsnäs, Värmdö" },
      ],
    },
    seoContent: {
      mainHeading: "Moving help on Värmdö – from Gustavsberg out towards the archipelago",
      mainText:
        "Värmdö is an archipelago municipality where homes range from the apartment blocks of Gustavsberg, with its history as a porcelain-works town on Farstaviken, to villas and terraced houses in Hemmesta and Brunn and thousands of houses first built as summer cottages. Many of them are now year-round homes, on Ingarö, around Djurö and out towards Stavsnäs. That makes moves here different from those in town: the road up to the house is often a narrow gravel track, the plot slopes towards the water and it can be some distance to carry from where the van can stop. We ask about this in advance so the right number of carriers comes and there is enough time.\n\nThe most common move we do to Värmdö is from a flat in Stockholm or Nacka to a house on the island – often a family wanting more space and to be near the water. The way out goes over Skurubron and along Värmdöleden (road 222), which continues past Mölnvik and Strömma to Stavsnäs. To Ingarö we take road 646 from Gustavsberg. The distance from central Stockholm is longer than the 20 km included in the package price, so expect a per-kilometre charge; you see the full amount before you book.\n\nVärmdö is made up of many islands, but we only move to addresses reachable by car. Houses on islands without a road link – for example on Sandön or Möja – need boat transport, which we don't offer. If you live on a private road run by a road association, check whether there are limits for heavier vehicles, especially during the spring thaw, and tell us when you book.",
      preparationHeading: "Prepare your move on Värmdö",
      preparationText:
        "Pack loose items in boxes and label them by room. Empty wardrobes and dressers and defrost the freezer the day before. Can the van drive right up to the house? If not, show us where it can stand and we carry from there – but it takes longer, so choose a package that covers it.\n\nCheck that the road to the plot is passable for a van: gates should open, branches shouldn't hang too low and the turning area should be clear. In Gustavsberg and other apartment areas, find out where the loading spot is and whether the lift can be locked. Don't forget the shed, guest cottage and boathouse if things from there are coming along. Have keys and codes ready for both addresses.",
      additionalSections: [
        {
          heading: "Places we move in, on Värmdö",
          content:
            "We move to and from every address on Värmdö that can be reached by car, including Gustavsberg, Hemmesta, Mölnvik, Brunn, Ingarö, Djurö and Stavsnäs.\n\nWe also move between Värmdö and the rest of the Stockholm region – Nacka, central Stockholm and the southern suburbs are the most common. The package price includes 20 km of driving, and further driving is charged per kilometre according to the package.",
        },
        {
          heading: "From summer house to year-round home",
          content:
            "Many moves on Värmdö are about a house becoming a permanent home. Often a whole household from town has to go into a house that already has some furniture – and something has to go. We can move the new things in and at the same time take the old to recycling if you also book junk removal.",
          cta: { text: "See junk removal in Stockholm →", href: "/en/junk-removal-stockholm" },
        },
        {
          heading: "Why book moving help on Värmdö with Ungdomskraft?",
          listItems: [
            { label: "Used to gravel roads and slopes –", text: "We plan for the van not always being at the door, and would rather carry an extra round than rush." },
            { label: "Price including driving –", text: "You see the package price and per-km charge straight away, before booking. No quote, no hourly rate." },
            { label: "Clear limits –", text: "We drive to every address with a road. Islands that need a boat we don't take, and you'll know straight away." },
            { label: "Insured –", text: "Jobs are insured through Trygg Hansa." },
            { label: "RUT deduction –", text: "Half the labour cost is deducted on the invoice, up to SEK 75,000 per person per year." },
          ],
        },
      ],
    },
    faq: [
      { question: "How much does moving help on Värmdö cost?", answer: "The packages cost Small SEK 995, Medium SEK 1,495, Large SEK 2,495, Extra Large SEK 4,995 (up to 4 h) and Mega Large SEK 8,995 (up to 8 h). 20 km of driving is included, and longer driving, which is often the case out to Värmdö, is charged per kilometre. The total shows before you book." },
      { question: "Why is there a per-km charge to Värmdö?", answer: "The package price includes 20 km of driving. Many addresses on Värmdö are further from town than that, and the extra kilometres are charged at the package's per-km rate." },
      { question: "Can you move me to a house on an island without a bridge?", answer: "No. We drive to every address reachable by car, but don't offer boat transport to islands like Sandön or Möja." },
      { question: "Can you drive on narrow gravel roads?", answer: "Yes, our vans handle normal gravel roads. Tell us if the road is narrow, steep or has no turning area, or if the road association has restrictions." },
      { question: "Can I get a RUT deduction on moving help on Värmdö?", answer: "Yes, 50% of the labour cost, up to SEK 75,000 per person per year. We apply it on the invoice." },
      { question: "What if the van can't park at the house?", answer: "Then we carry from the nearest spot where the van can stand. It takes more time, so describe the distance when booking and choose a package that covers it." },
      { question: "How soon can you come out to Värmdö?", answer: "Often within a few days. Free times for your addresses are shown in the booking." },
      { question: "Is a van included?", answer: "Yes, from the Small package upwards a van and two carriers are included." },
      { question: "Do you move appliances?", answer: "Yes – fridge, freezer, washing machine and tumble dryer. Defrost and empty them the day before." },
      { question: "Do you take apart and reassemble furniture?", answer: "Yes, anything that won't fit whole through doors and stairways we take apart and reassemble on site." },
      { question: "Can you move at the weekend?", answer: "Yes, we move every day of the week." },
      { question: "Will you take things from a shed or boathouse?", answer: "Yes, if it's coming to the new address we carry it. Mention it when booking." },
      { question: "Which parts of Värmdö do you cover?", answer: "Every address with a road link, including Gustavsberg, Hemmesta, Mölnvik, Brunn, Ingarö, Djurö and Stavsnäs." },
      { question: "Which other municipalities do you serve?", answer: `The whole Stockholm region: ${stockholmKommunerText}. Same pricing everywhere – package price plus driving beyond 20 km.` },
    ],
    priceHint: PACKAGE_PRICE_HINT_EN.transport,
    pricingRows: getPackagePricingRows("transport", "en"),
    dynamicHeadings: {
      threeSteps: "Moving help on Värmdö in 3 steps",
      step2Text:
        "The carriers load the van at your old address and drive out along Värmdöleden to the house.",
      step3Title: "Moved in",
      step3Desc:
        "We carry everything in, from the road too if the van can't get all the way, and reassemble what was taken apart.",
      ctaHeading: "Book moving help on Värmdö",
      ctaText:
        "Enter the addresses, choose a package and time. You see the price including driving before you book.",
    },
  },
};
